'use client'
import React from 'react'
import {
  DcFlexBox,
  PixelCurrency,
  PixelProgressBar,
  PixelTooltip,
} from 'pixel-base'
import { SectionContent } from 'pixel-base'
import { colors } from 'pixel-base'
import styled from 'styled-components'
import { ClockIcon } from 'utils/icons/clock-icon'
import { MoneyIcon } from 'utils/icons/money-icon'
import { TruckIcon } from 'utils/icons/truck-icon'
import { UserIcon } from 'utils/icons/user-icon'
import { WarehouseIcon } from 'utils/icons/warehouse-icon'
import { WeightIcon } from 'utils/icons/weight-icon'

interface DispatchData {
  dispatchId: string
  dispatchDate: string
  warehouse: string
  cod: string
  packages: number
  pieces: number
  weight: string
  createdBy: string
  date: string
  courier: string
  status: 'completed' | 'pending' | 'failed'
  processing_time_stats?: {
    green: number
    yellow: number
    red: number
  }
}

interface SummaryCardProps {
  dispatchData: DispatchData
}

interface CardContentSectionProps {
  columns?: string
}

interface BadgeProps {
  bgColor?: string
  textColor?: string
}

const CardHeader = styled.div`
  background-color: ${colors.grayLighter};
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${colors.grayBorderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CardTitle = styled.h2`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${colors.grayTextDark};
  margin: 0;
`

const CardSubtitle = styled.p`
  font-size: 0.75rem;
  color: ${colors.grayTextMedium};
  margin: 0;
`

const CardContentSection = styled.div<CardContentSectionProps>`
  padding: 0.75rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: ${(props) => props.columns || 'repeat(4, 1fr)'};
  }
`

const Divider = styled.div`
  height: 1px;
  background-color: ${colors.grayBorderLight};
`

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`

const IconWrapper = styled.div`
  display: flex;
  height: 1.75rem;
  width: 1.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background-color: ${colors.blueLightBg};
  flex-shrink: 0;
`

const Icon = styled.svg`
  height: 1rem;
  width: 1rem;
  color: ${colors.primary};
`

const StatusIcon = styled(Icon)`
  color: ${(props) => props.color || colors.success};
`

const InfoContent = styled.div`
  min-width: 0;
`

const InfoLabel = styled.p`
  font-size: 0.75rem;
  color: ${colors.grayTextLight};
  margin: 0;
`

const InfoValue = styled.p`
  font-weight: 500;
  color: ${colors.grayTextDark};
  margin: 0;
  font-size: 0.75rem;
`

const InfoGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
`

const Badge = styled.span<BadgeProps>`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  background-color: ${(props) => props.bgColor || colors.blueLightBg};
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) => props.textColor || colors.primary};
`

const CardFooter = styled.div`
  background-color: ${colors.grayLighter};
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StatusMessage = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) => props.color || colors.success};
`

const ProcessingTimeTooltip = styled.div`
  padding: 0.5rem;
  background-color: ${colors.grayTextDark};
  color: white;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  line-height: 1.25rem;
`

const ProcessingTimeTooltipItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const ProcessingTimeDot = styled.div<{ color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: ${(props) => props.color};
`

const SummaryCard: React.FC<SummaryCardProps> = ({ dispatchData }) => {
  const getStatusStyles = () => {
    switch (dispatchData.status) {
      case 'completed':
        return {
          icon: 'M5 13l4 4L19 7',
          textColor: colors.success,
          bgColor: colors.successLight,
          textBadge: colors.successDark,
          message: 'All items processed successfully',
        }
      case 'pending':
        return {
          icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
          textColor: colors.warning,
          bgColor: colors.warningLight,
          textBadge: colors.warningDark,
          message: 'Processing in progress',
        }
      case 'failed':
        return {
          icon: 'M6 18L18 6M6 6l12 12',
          textColor: colors.danger,
          bgColor: colors.dangerLight,
          textBadge: colors.dangerDark,
          message: 'Processing failed',
        }
      default:
        return {
          icon: 'M5 13l4 4L19 7',
          textColor: colors.success,
          bgColor: colors.successLight,
          textBadge: colors.successDark,
          message: 'All items processed successfully',
        }
    }
  }

  const statusStyles = getStatusStyles()

  const totalItems = dispatchData.processing_time_stats
    ? dispatchData.processing_time_stats.green +
      dispatchData.processing_time_stats.yellow +
      dispatchData.processing_time_stats.red
    : 0

  const processingTimeStats = dispatchData.processing_time_stats
    ? [
        {
          value: dispatchData.processing_time_stats.green,
          color: colors.success,
          label: 'Within 3 days',
          percentage:
            (dispatchData.processing_time_stats.green / totalItems) * 100,
        },
        {
          value: dispatchData.processing_time_stats.yellow,
          color: colors.warning,
          label: 'Within 5 days',
          percentage:
            (dispatchData.processing_time_stats.yellow / totalItems) * 100,
        },
        {
          value: dispatchData.processing_time_stats.red,
          color: colors.danger,
          label: 'More than 5 days',
          percentage:
            (dispatchData.processing_time_stats.red / totalItems) * 100,
        },
      ]
    : []

  const renderProcessingTimeTooltip = () => (
    <ProcessingTimeTooltip>
      <ProcessingTimeTooltipItem>
        <ProcessingTimeDot color={colors.success} />
        <span>
          {dispatchData.processing_time_stats?.green} fulfilled within 3 days
        </span>
      </ProcessingTimeTooltipItem>
      <ProcessingTimeTooltipItem>
        <ProcessingTimeDot color={colors.warning} />
        <span>
          {dispatchData.processing_time_stats?.yellow} fulfilled within 5 days
        </span>
      </ProcessingTimeTooltipItem>
      <ProcessingTimeTooltipItem>
        <ProcessingTimeDot color={colors.danger} />
        <span>
          {dispatchData.processing_time_stats?.red} fulfilled in more than 5
          days
        </span>
      </ProcessingTimeTooltipItem>
    </ProcessingTimeTooltip>
  )

  return (
    <SectionContent>
      <CardHeader>
        <div>
          <CardTitle>Dispatch Details</CardTitle>
          <CardSubtitle>
            ID: {dispatchData.dispatchId} • {dispatchData.dispatchDate}
          </CardSubtitle>
        </div>
        <Badge
          bgColor={statusStyles.bgColor}
          textColor={statusStyles.textBadge}
        >
          {dispatchData.status.charAt(0).toUpperCase() +
            dispatchData.status.slice(1)}
        </Badge>
      </CardHeader>

      <CardContentSection>
        {/* Warehouse */}
        <InfoItem>
          <IconWrapper>
            <WarehouseIcon />
          </IconWrapper>
          <InfoContent>
            <InfoLabel>Warehouse</InfoLabel>
            <InfoValue>{dispatchData.warehouse}</InfoValue>
          </InfoContent>
        </InfoItem>

        {/* COD */}
        <InfoItem>
          <IconWrapper>
            <MoneyIcon />
          </IconWrapper>
          <InfoContent>
            <InfoLabel>COD</InfoLabel>
            <InfoValue>
              <PixelCurrency value={parseFloat(dispatchData.cod)} />
            </InfoValue>
          </InfoContent>
        </InfoItem>

        {/* Packages & Pieces */}
        <InfoItem>
          <IconWrapper>
            <TruckIcon />
          </IconWrapper>
          <InfoGroup>
            <InfoContent>
              <InfoLabel>Packages</InfoLabel>
              <InfoValue>{dispatchData.packages}</InfoValue>
            </InfoContent>
            <InfoContent>
              <InfoLabel>Pieces</InfoLabel>
              <InfoValue>{dispatchData.pieces}</InfoValue>
            </InfoContent>
          </InfoGroup>
        </InfoItem>

        {/* Weight */}
        <InfoItem>
          <IconWrapper>
            <WeightIcon />
          </IconWrapper>
          <InfoContent>
            <InfoLabel>Weight</InfoLabel>
            <InfoValue>{dispatchData.weight}</InfoValue>
          </InfoContent>
        </InfoItem>
      </CardContentSection>

      <Divider />

      <CardContentSection columns="repeat(3, 1fr)">
        <InfoItem>
          <IconWrapper>
            <UserIcon />
          </IconWrapper>
          <InfoContent>
            <InfoLabel>Create By</InfoLabel>
            <InfoValue>{dispatchData.createdBy}</InfoValue>
          </InfoContent>
        </InfoItem>

        <InfoItem>
          <IconWrapper>
            <TruckIcon />
          </IconWrapper>
          <InfoContent>
            <InfoLabel>Courier</InfoLabel>
            <Badge bgColor={colors.blueLightBg} textColor={colors.primary}>
              {dispatchData.courier}
            </Badge>
          </InfoContent>
        </InfoItem>

        {/* Processing Time Stats */}
        {dispatchData.processing_time_stats && (
          <InfoItem>
            <IconWrapper>
              <ClockIcon />
            </IconWrapper>
            <InfoContent>
              <InfoLabel>Processing Time</InfoLabel>
              <DcFlexBox
                alignItems="center"
                gap="10px"
                style={{ marginTop: '4px' }}
              >
                <PixelTooltip
                  tooltipText={renderProcessingTimeTooltip()}
                  tooltipPlacement="top"
                >
                  <PixelProgressBar
                    stats={processingTimeStats}
                    tooltipFormatter={(stat) =>
                      `${stat.value} ${stat.label} (${stat.percentage.toFixed(
                        1,
                      )}%)`
                    }
                  />
                </PixelTooltip>
              </DcFlexBox>
            </InfoContent>
          </InfoItem>
        )}
      </CardContentSection>

      <Divider />

      <CardFooter>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <StatusIcon
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            color={statusStyles.textColor}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={statusStyles.icon}
            />
          </StatusIcon>
          <StatusMessage color={statusStyles.textColor}>
            {statusStyles.message}
          </StatusMessage>
        </div>
      </CardFooter>
    </SectionContent>
  )
}

export default SummaryCard
